import { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../utils/api';

/**
 * Tiny inline SVG sparkline for a symbol. Pulls recent price snapshots
 * from /api/prices/history/{symbol} and draws them as a smooth polyline.
 */
export default function Sparkline({ symbol, trend = 'up', width = 96, height = 28, strokeWidth = 1.5, range = '24h', className = '' }) {
  const [points, setPoints] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const fetchHistory = async () => {
      try {
        const r = await axios.get(
          `${API}/prices/history/${encodeURIComponent(symbol)}?range=${encodeURIComponent(range)}&limit=250`,
          { timeout: 8000 }
        );
        const data = (r.data && Array.isArray(r.data.points)) ? r.data.points : [];
        if (!cancelled) setPoints(data);
      } catch (e) {
        if (!cancelled) setPoints([]);
      }
    };
    fetchHistory();
    const id = setInterval(fetchHistory, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, [symbol, range]);

  if (!points || points.length < 2) {
    return <div style={{ width, height }} className={`opacity-40 ${className}`} data-testid={`sparkline-${symbol}-empty`} />;
  }

  const values = points.map(p => Number(p.p)).filter(v => Number.isFinite(v));
  if (values.length < 2) {
    return <div style={{ width, height }} className={`opacity-40 ${className}`} />;
  }
  const min = Math.min(...values);
  const max = Math.max(...values);
  const valueRange = max - min || 1;

  const stepX = width / (values.length - 1);
  const path = values.map((v, i) => {
    const x = i * stepX;
    const y = height - ((v - min) / valueRange) * (height - 2) - 1;
    return `${i === 0 ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)}`;
  }).join(' ');

  const color = trend === 'up' ? '#00E676' : '#FF3B30';
  const areaPath = `${path} L${width},${height} L0,${height} Z`;
  const gradientId = `spark-grad-${symbol}-${trend}`;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width={width}
      height={height}
      className={className}
      preserveAspectRatio="none"
      data-testid={`sparkline-${symbol}`}
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#${gradientId})`} />
      <path d={path} fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
